import { Link } from "react-router-dom";
import Seo from "@/components/feature/Seo";
import PageHeader from "@/components/feature/PageHeader";
import { siteMeta, feedback } from "@/mocks/manga";

const values = [
  { icon: "ri-list-check-2", title: "目录清晰", desc: "按单行本卷与话数整理，章节一目了然，回看任意一段都很方便。" },
  { icon: "ri-magic-line", title: "全彩收录", desc: "认真整理全彩珍藏版本，让名场面的每一处细节都能被看见。" },
  { icon: "ri-gift-line", title: "番外齐全", desc: "新婚篇、童年篇、情人节篇等番外独立归类，正篇番外兼顾。" },
  { icon: "ri-heart-3-line", title: "用心运营", desc: "持续跟进连载更新与番外企划，让读者始终能找到完整内容。" },
];

export default function About() {
  const aboutSchema = {
    "@context": "https://schema.org",
    "@type": "AboutPage",
    name: "关于我们",
    description: `${siteMeta.name}的品牌介绍、网站定位、发展理念、内容原则与联系方式。`,
  };

  const orgSchema = {
    "@context": "https://schema.org",
    "@type": "Organization",
    name: siteMeta.name,
    description: siteMeta.desc,
  };

  return (
    <>
      <Seo
        title={`关于我们 - ${siteMeta.name}`}
        description={`了解${siteMeta.name}：专注《甜蜜惩罚》漫画单行本全卷、连载话、全彩与番外收录，以清晰目录服务每一位读者。`}
        keywords={siteMeta.keywords}
        canonicalPath="/about"
        schema={[aboutSchema, orgSchema]}
      />

      <PageHeader
        title="关于我们"
        subtitle="了解甜蜜惩罚漫画网的定位、理念与内容原则。"
        crumbs={[{ label: "首页", to: "/" }, { label: "关于我们" }]}
      />

      <div className="bg-background-50 py-12 md:py-16">
        <div className="mx-auto max-w-7xl px-4 md:px-6">
          <section className="grid grid-cols-1 gap-10 lg:grid-cols-2">
            <div>
              <span className="text-xs font-semibold uppercase tracking-widest text-primary-600">品牌介绍</span>
              <h2 className="mt-3 font-heading text-2xl font-bold text-foreground-950 md:text-3xl">
                专注《甜蜜惩罚》，让找齐整部作品更简单
              </h2>
              <p className="mt-5 text-sm leading-7 text-foreground-600 md:text-base md:leading-8">
                {siteMeta.name}是一处以《甜蜜惩罚》漫画为核心的阅读整理站点。我们深知追一部作品时，
                最怕的就是章节混乱、番外难寻。因此我们按单行本卷与话数整理了完整的目录结构，
                把单行本全卷、连载话、全彩版本与番外一并收纳其中，让每一位读者都能轻松定位到自己想读的内容。
              </p>
              <p className="mt-4 text-sm leading-7 text-foreground-600 md:text-base md:leading-8">
                无论是第一次接触这部作品的新读者，还是想要重温某段心动瞬间的老读者，
                我们都希望这里能成为你了解与阅读《甜蜜惩罚》最顺手的一站。
              </p>
            </div>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              {values.map((v) => (
                <div key={v.title} className="rounded-2xl bg-background-100 p-5">
                  <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-primary-100 text-primary-600">
                    <i className={`${v.icon} text-xl`}></i>
                  </span>
                  <h3 className="mt-4 font-heading text-base font-bold text-foreground-950">{v.title}</h3>
                  <p className="mt-1.5 text-sm leading-relaxed text-foreground-600">{v.desc}</p>
                </div>
              ))}
            </div>
          </section>

          <section className="mt-16 rounded-3xl bg-background-100 p-6 md:p-10">
            <div className="grid grid-cols-1 gap-8 lg:grid-cols-3">
              <div>
                <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-accent-100 text-accent-700">
                  <i className="ri-compass-3-line text-2xl"></i>
                </span>
                <h3 className="mt-4 font-heading text-lg font-bold text-foreground-950">网站定位</h3>
                <p className="mt-2 text-sm leading-relaxed text-foreground-600">
                  专注于单一作品的深度整理型阅读站点，以清晰、完整、易检索为第一目标。
                </p>
              </div>
              <div>
                <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-accent-100 text-accent-700">
                  <i className="ri-flower-line text-2xl"></i>
                </span>
                <h3 className="mt-4 font-heading text-lg font-bold text-foreground-950">发展理念</h3>
                <p className="mt-2 text-sm leading-relaxed text-foreground-600">
                  用认真而细腻的态度对待每一次更新与整理，让内容井井有条，让阅读回归纯粹与愉悦。
                </p>
              </div>
              <div>
                <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-accent-100 text-accent-700">
                  <i className="ri-shield-check-line text-2xl"></i>
                </span>
                <h3 className="mt-4 font-heading text-lg font-bold text-foreground-950">内容原则</h3>
                <p className="mt-2 text-sm leading-relaxed text-foreground-600">
                  真实、准确地呈现作品信息与目录结构，不夸大、不虚构，以读者体验为最高准则。
                </p>
              </div>
            </div>
          </section>

          <section className="mt-16">
            <div className="mx-auto max-w-2xl text-center">
              <span className="text-xs font-semibold uppercase tracking-widest text-primary-600">用户口碑</span>
              <h2 className="mt-3 font-heading text-2xl font-bold text-foreground-950 md:text-3xl">读者怎么说</h2>
            </div>
            <div className="mt-8 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
              {feedback.map((f) => (
                <figure key={f.name} className="rounded-2xl bg-background-100 p-6">
                  <blockquote className="text-sm leading-relaxed text-foreground-700">{f.content}</blockquote>
                  <figcaption className="mt-4 text-sm font-semibold text-foreground-900">{f.name}</figcaption>
                </figure>
              ))}
            </div>
          </section>

          <section className="mt-16 flex flex-col items-center justify-between gap-5 rounded-3xl bg-gradient-to-r from-primary-100/70 to-accent-100/60 p-8 md:flex-row md:p-10">
            <div>
              <h2 className="font-heading text-xl font-bold text-foreground-950 md:text-2xl">有任何疑问或建议？</h2>
              <p className="mt-2 text-sm text-foreground-600">欢迎随时联系我们，一起把这座甜蜜的漫画站做得更好。</p>
            </div>
            <Link
              to="/contact"
              className="inline-flex items-center gap-2 rounded-full bg-primary-500 px-7 py-3 text-sm font-semibold whitespace-nowrap text-background-50 transition-colors hover:bg-primary-600"
            >
              联系我们
              <i className="ri-arrow-right-line"></i>
            </Link>
          </section>
        </div>
      </div>
    </>
  );
}